import {
  AloftPrice,
  AloftPriceJSON,
  SourceType,
  SubjectData,
  orUndefined,
  schAloftPrice,
  schSourceType,
  schUnitRateModels,
} from '@aloft/toolkit-shared';
import {
  Block,
  Flex,
  Icon,
  IconName,
  ProductColor,
  Tooltip,
  Typography,
  theme,
} from '@aloft/toolkit-sui';
// eslint-disable-next-line aloft/no-sui-chakra, aloft/prefer-sound-ui-button
import {
  Button,
  ButtonProps,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@aloft/toolkit-sui/chakra';

import React, { useRef, useState } from 'react';
import { Controller, ControllerRenderProps, FieldPath, useWatch } from 'react-hook-form';
import { useParams } from 'react-router';

import { useGetTkv2UnitRateModelsAPI } from '../../../hooks/api/toolkitComponents.ts';
import { featuresSelectors } from '../../../redux/features/selectors/featuresSelectors.ts';
import {
  isRegressionModelReliableForFeature,
  isSensitivityModelReliableForFeature,
} from '../../../redux/features/selectors/propertyDataSelectors/unitRateModelSelectors.ts';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks.ts';
import { PropertyGridForm } from '../types.ts';
import {
  getDataSourceDisplayInfo,
  getSelectSourceTooltip,
  handleUpdatePropertyData,
} from '../utils.ts';
import { editableFieldOutlineSx, readOnlyFieldTypographyProps } from './consts.ts';
import { PropertyGridFieldProps } from './types.ts';

type SharedEditableUnitRateFieldProps = PropertyGridFieldProps & {
  unitRateFieldName: FieldPath<PropertyGridForm>;
  displayRate?: AloftPrice;
  storedUnitRate?: AloftPriceJSON;
  rawInputValue?: string;
  setRawInputValue: React.Dispatch<React.SetStateAction<string | undefined>>;
  isFocused: boolean;
  setIsFocused: React.Dispatch<React.SetStateAction<boolean>>;
  handleSetFieldValue: (onChange: ControllerRenderProps['onChange'], newValue?: string) => void;
};

type UseUnitRateInputProps = Pick<
  SharedEditableUnitRateFieldProps,
  | 'displayRate'
  | 'storedUnitRate'
  | 'rawInputValue'
  | 'setRawInputValue'
  | 'isFocused'
  | 'setIsFocused'
  | 'handleSetFieldValue'
>;

// Shared hook — input state + event handlers
const useUnitRateInput = ({
  displayRate,
  storedUnitRate,
  rawInputValue,
  setRawInputValue,
  isFocused,
  setIsFocused,
  handleSetFieldValue,
}: UseUnitRateInputProps) => {
  const rateString = displayRate?.toString(true);

  const inputValue = (() => {
    const v = isFocused && rawInputValue !== undefined ? rawInputValue : (rateString ?? '');
    return v.replace(/^(-?)\$/, '$1');
  })();

  const handleFocus = () => {
    setIsFocused(true);
    const raw = rateString?.replace(/-?\$/, (m) => (m === '-$' ? '-' : '')) ?? '';
    setRawInputValue(raw);
  };

  const commitValue = (onChange: ControllerRenderProps['onChange']) => {
    const inputToParse = (rawInputValue ?? rateString ?? '').trim();
    try {
      if (
        !inputToParse ||
        inputToParse === '-' ||
        AloftPrice.fromDollarLike(inputToParse).equals(AloftPrice.zero)
      ) {
        handleSetFieldValue(onChange, undefined);
      } else {
        handleSetFieldValue(onChange, inputToParse);
      }
    } catch {
      handleSetFieldValue(onChange, rateString);
    }
    setRawInputValue(undefined);
  };

  const handleBlur = (onChange: ControllerRenderProps['onChange'], onClose?: () => void) => {
    setIsFocused(false);
    commitValue(onChange);
    onClose?.();
  };

  const handleKeyDown = (
    e: React.KeyboardEvent,
    onChange: ControllerRenderProps['onChange'],
    onClose?: () => void,
  ) => {
    if (e.key === 'Escape') {
      setRawInputValue(undefined);
      setIsFocused(false);
      onChange(storedUnitRate ?? undefined);
      onClose?.();
    } else if (e.key === 'Enter' || e.key === 'NumpadEnter') {
      (e.target as HTMLInputElement).blur();
    }
  };

  return { rateString, inputValue, handleFocus, handleBlur, handleKeyDown };
};

const labelTypographySx = { flexShrink: 0, lineHeight: '1' } as const;

type UnitRateInputRowProps = {
  label: string;
  inputValue: string;
  inputRef: React.Ref<HTMLInputElement>;
  inputStyle?: React.CSSProperties;
  onFocus: () => void;
  onBlur: () => void;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onKeyDown: (e: React.KeyboardEvent) => void;
};

const UnitRateInputRow = ({
  label,
  inputValue,
  inputRef,
  inputStyle,
  onFocus,
  onBlur,
  onChange,
  onKeyDown,
}: UnitRateInputRowProps) => (
  <>
    <Typography variant="body2" inline color="textSecondary" __dangerDoNotUseSx={labelTypographySx}>
      $
    </Typography>
    <input
      ref={inputRef}
      type="text"
      value={inputValue}
      placeholder="-"
      style={{
        flex: 1,
        minWidth: 0,
        background: 'transparent',
        border: 'none',
        outline: 'none',
        lineHeight: '1',
        padding: '0',
        ...inputStyle,
      }}
      onFocus={onFocus}
      onBlur={onBlur}
      onChange={onChange}
      onKeyDown={onKeyDown}
    />
    <Typography variant="body2" inline color="textSecondary" __dangerDoNotUseSx={labelTypographySx}>
      {label}
    </Typography>
  </>
);

type SourceIconSlotProps = {
  show: boolean;
  icon: IconName;
  tooltipLabel: string;
};

const SourceIconSlot = ({ show, icon, tooltipLabel }: SourceIconSlotProps) =>
  show ? (
    <Tooltip label={tooltipLabel}>
      <Icon icon={icon} />
    </Tooltip>
  ) : (
    <div style={{ width: '16px', height: '16px' }} aria-hidden />
  );

const UnitRatePlaceholderField = ({ colSpan }: { colSpan: number }) => (
  <Typography
    {...readOnlyFieldTypographyProps}
    __dangerDoNotUseSx={{
      ...readOnlyFieldTypographyProps.__dangerDoNotUseSx,
      gridColumn: `span ${colSpan}`,
    }}
  >
    -
  </Typography>
);

const EditableUnitRateFieldInner = ({
  columnIndex,
  field,
  control,
  getValues,
  setValue,
  colSpan = 1,
}: PropertyGridFieldProps) => {
  const dispatch = useAppDispatch();

  const { selectShouldShowAdjustmentRegression } = featuresSelectors;
  const shouldShowAdjustmentRegression = useAppSelector(selectShouldShowAdjustmentRegression);

  const { unitRate } = field;
  const { key: unitRateKey } = unitRate!;

  const propertyId = useWatch({ control, name: `columns.${columnIndex}.propertyId` });
  const unitRateFieldName =
    `columns.${columnIndex}.propertyData.editableData.salesComparison.unitRates.${unitRateKey}` as FieldPath<PropertyGridForm>;
  const selectedSourceFieldName =
    `columns.${columnIndex}.propertyData.selectedSources.editableData.salesComparison.unitRates.${unitRateKey}` as FieldPath<PropertyGridForm>;

  const storedUnitRate = useWatch({ control, name: unitRateFieldName }) as
    | AloftPriceJSON
    | undefined;

  const [rawInputValue, setRawInputValue] = useState<string | undefined>(undefined);
  const [isFocused, setIsFocused] = useState(false);

  const handleSetFieldValue = (onChange: ControllerRenderProps['onChange'], newValue?: string) => {
    const parsedRate = newValue ? AloftPrice.fromDollarLike(newValue) : undefined;
    onChange(parsedRate?.toJSON());

    const propertyData = getValues(`columns.${columnIndex}.propertyData`);
    const subjectPropertyData = propertyData as SubjectData | undefined;
    if (subjectPropertyData?.editableData) {
      if (!subjectPropertyData.editableData.salesComparison) {
        subjectPropertyData.editableData.salesComparison = { unitRates: {} };
      } else if (!subjectPropertyData.editableData.salesComparison.unitRates) {
        subjectPropertyData.editableData.salesComparison.unitRates = {};
      }
      subjectPropertyData.editableData.salesComparison.unitRates![unitRateKey] =
        parsedRate?.toJSON();
    }
    handleUpdatePropertyData(propertyId, getValues(), dispatch, subjectPropertyData);
  };

  const displayRate = orUndefined(schAloftPrice.parse)(storedUnitRate);

  const sharedProps: SharedEditableUnitRateFieldProps = {
    columnIndex,
    field,
    control,
    getValues,
    setValue,
    colSpan,
    unitRateFieldName,
    displayRate,
    storedUnitRate,
    handleSetFieldValue,
    rawInputValue,
    setRawInputValue,
    isFocused,
    setIsFocused,
  };

  if (shouldShowAdjustmentRegression) {
    return (
      <EditableUnitRateFieldWithSources
        {...sharedProps}
        selectedSourceFieldName={selectedSourceFieldName}
      />
    );
  }

  return <LegacyEditableUnitRateField {...sharedProps} />;
};

// Legacy variant (no popover, plain inline input)
const LegacyEditableUnitRateField = ({
  field,
  control,
  colSpan = 1,
  unitRateFieldName,
  displayRate,
  storedUnitRate,
  handleSetFieldValue,
  rawInputValue,
  setRawInputValue,
  isFocused,
  setIsFocused,
}: SharedEditableUnitRateFieldProps) => {
  const { unitRate } = field;
  const { label } = unitRate!;

  return (
    <Controller
      name={unitRateFieldName}
      control={control}
      render={({ field: { onChange, ref } }) => {
        const { inputValue, handleFocus, handleBlur, handleKeyDown } = useUnitRateInput({
          displayRate,
          storedUnitRate,
          rawInputValue,
          setRawInputValue,
          isFocused,
          setIsFocused,
          handleSetFieldValue,
        });

        const hasValue = displayRate && !displayRate.equals(AloftPrice.zero);
        const borderStyle = hasValue
          ? `1px dashed ${theme.colors.buttonDefault}`
          : '1px solid transparent';

        return (
          <Block
            width="100%"
            height="100%"
            gridColumn={`span ${colSpan}`}
            backgroundColor="textOffset"
            __dangerDoNotUseSx={{
              display: 'flex',
              alignItems: 'center',
              border: borderStyle,
              outline: 'none',
              cursor: 'text',
              _focusWithin: {
                outline: '1.5px solid',
                outlineOffset: '-1.5px',
                outlineColor: theme.colors.buttonDefault,
                background: theme.colors.surface3,
                border: '1px solid transparent',
              },
              _hover: {
                outline: '1.5px solid',
                outlineOffset: '-1.5px',
                outlineColor: theme.colors.buttonDefault,
              },
            }}
          >
            <UnitRateInputRow
              label={label}
              inputValue={inputValue}
              inputRef={ref}
              inputStyle={{
                fontSize: '12px',
                color: displayRate?.getDisplayColor() as ProductColor | undefined,
                padding: '0 2px',
                textAlign: 'left',
              }}
              onFocus={handleFocus}
              onBlur={() => handleBlur(onChange)}
              onChange={(e) => setRawInputValue(e.target.value.replace(/^0+(?=\d)/, ''))}
              onKeyDown={(e) => handleKeyDown(e, onChange)}
            />
          </Block>
        );
      }}
    />
  );
};

// With-sources variant (popover + source selector)
const sharedButtonStyle: Partial<ButtonProps> = {
  width: '100%',
  height: '32px',
  borderRadius: '0',
  color: theme.colors.textPrimary,
  padding: '4px 8px',
  fontWeight: 'normal',
  ...editableFieldOutlineSx,
  _active: {},
  _focus: {},
  _focusWithin: {},
  _hover: { backgroundColor: theme.colors.surface2 },
};

const EditableUnitRateFieldWithSources = ({
  columnIndex,
  field,
  control,
  getValues,
  setValue,
  colSpan = 1,
  unitRateFieldName,
  selectedSourceFieldName,
  displayRate,
  storedUnitRate,
  handleSetFieldValue,
  rawInputValue,
  setRawInputValue,
  isFocused,
  setIsFocused,
}: SharedEditableUnitRateFieldProps & {
  selectedSourceFieldName: FieldPath<PropertyGridForm>;
}) => {
  const { unitRate } = field;
  const { label, key: unitRateKey } = unitRate!;

  const orderID = useParams().orderId!;

  const propertyId = useWatch({ control, name: `columns.${columnIndex}.propertyId` });

  const customInput = useRef<HTMLInputElement>(
    null,
  ) as React.MutableRefObject<HTMLInputElement | null>;

  const columns = getValues('columns');

  const unitRateModelsQuery = useGetTkv2UnitRateModelsAPI(
    { pathParams: { orderID } },
    { staleTime: Infinity },
  );
  const unitRateModels = schUnitRateModels.safeParse(unitRateModelsQuery.data).data ?? null;
  const isRegressionReliable = isRegressionModelReliableForFeature(unitRateModels, unitRateKey);
  const isSensitivityReliable = isSensitivityModelReliableForFeature(unitRateModels, unitRateKey);

  const modelOptions = columns
    .filter(
      (column) =>
        column?.propertyId === propertyId &&
        column?.type !== 'property' &&
        schSourceType.safeParse(column?.type).success,
    )
    .map((column) => {
      const source = column.type as SourceType;
      const dataSourceColumnIndex = columns.findIndex(
        (col) => col?.propertyId === propertyId && col?.type === source,
      );
      if (dataSourceColumnIndex === -1) return null;

      const optionUnitRate = getValues(
        `columns.${dataSourceColumnIndex}.propertyData.editableData.salesComparison.unitRates.${unitRateKey}` as FieldPath<PropertyGridForm>,
      ) as AloftPriceJSON | undefined;

      const parsedRate = orUndefined(schAloftPrice.parse)(optionUnitRate);
      if (!parsedRate) return null;

      return {
        value: parsedRate.toString(true),
        rawValue: parsedRate,
        source,
      };
    })
    .filter(
      (option): option is { value: string; rawValue: AloftPrice; source: SourceType } =>
        option !== null,
    );

  return (
    <Controller
      name={unitRateFieldName}
      control={control}
      render={({ field: { onChange, ref } }) => {
        const { rateString, inputValue, handleFocus, handleBlur, handleKeyDown } = useUnitRateInput(
          {
            displayRate,
            storedUnitRate,
            rawInputValue,
            setRawInputValue,
            isFocused,
            setIsFocused,
            handleSetFieldValue,
          },
        );

        const selectedSourceValue = (
          getValues(selectedSourceFieldName) as { source?: SourceType } | undefined
        )?.source;

        const isRegressionSource = selectedSourceValue === 'regression';
        const isSensitivitySource = selectedSourceValue === 'sensitivity';
        const showWarning =
          (isRegressionSource && !isRegressionReliable) ||
          (isSensitivitySource && !isSensitivityReliable);

        const backgroundColor = showWarning
          ? theme.colors.alertWarningLight
          : theme.colors.surface1;

        const hasValue = !!displayRate && !displayRate.equals(AloftPrice.zero);
        const parsedSource = schSourceType.safeParse(selectedSourceValue);
        const hasSelectedSource = parsedSource.success;
        const icon = hasSelectedSource
          ? getDataSourceDisplayInfo(parsedSource.data, undefined).icon
          : 'user-input';
        const iconTooltipLabel = hasSelectedSource
          ? getSelectSourceTooltip(parsedSource.data)
          : 'Custom';

        return (
          <Popover gutter={-32} initialFocusRef={customInput} variant="inline" isLazy matchWidth>
            {({ onClose }) => {
              const handleOnClose = () => {
                if (document.activeElement instanceof HTMLElement) document.activeElement?.blur();
                onClose();
              };

              return (
                <>
                  <PopoverTrigger>
                    <Button
                      {...sharedButtonStyle}
                      backgroundColor={backgroundColor}
                      gridColumn={`span ${colSpan}`}
                    >
                      <Flex alignItems="center" justifyContent="space-between" width="100%">
                        <Flex alignItems="center" gap="4px" minWidth="0" flex={1} overflow="hidden">
                          <Typography
                            variant="body2"
                            inline
                            color="textSecondary"
                            __dangerDoNotUseSx={labelTypographySx}
                          >
                            $
                          </Typography>
                          <Typography
                            variant="body2"
                            inline
                            truncate
                            __dangerDoNotUseSx={{
                              whiteSpace: 'nowrap',
                              flexShrink: 1,
                              minWidth: 0,
                              lineHeight: '1',
                            }}
                          >
                            {rateString ? rateString.replace(/^(-?)\$/, '$1') : '-'}
                          </Typography>
                          <span style={{ flex: 1 }} />
                          <Typography
                            variant="body2"
                            inline
                            color="textSecondary"
                            __dangerDoNotUseSx={labelTypographySx}
                          >
                            {label}
                          </Typography>
                        </Flex>
                        <Flex alignItems="center" gap="0.25rem">
                          <SourceIconSlot
                            show={hasValue}
                            icon={icon}
                            tooltipLabel={iconTooltipLabel}
                          />
                        </Flex>
                      </Flex>
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent borderRadius="0" width="100%">
                    <Flex
                      alignItems="center"
                      justifyContent="space-between"
                      width="100%"
                      height="32px"
                      gap="4px"
                      padding="4px 8px"
                      borderWidth="0px"
                      borderStyle="solid"
                      borderColor="strokeDefault"
                      borderBottomWidth="1px"
                      __dangerDoNotUseSx={{
                        ...(isFocused && {
                          outline: '1.5px solid',
                          outlineOffset: '-1.5px',
                          outlineColor: theme.colors.selected2,
                        }),
                      }}
                    >
                      <Flex alignItems="center" gap="4px" minWidth="0" flex={1}>
                        <UnitRateInputRow
                          label={label}
                          inputValue={inputValue}
                          inputRef={(instance) => {
                            ref(instance);
                            customInput.current = instance;
                          }}
                          inputStyle={{ fontSize: '14px' }}
                          onFocus={handleFocus}
                          onBlur={() => handleBlur(onChange, handleOnClose)}
                          onChange={(e) =>
                            setRawInputValue(e.target.value.replace(/^0+(?=\d)/, ''))
                          }
                          onKeyDown={(e) => handleKeyDown(e, onChange, onClose)}
                        />
                      </Flex>
                      <SourceIconSlot show={hasValue} icon={icon} tooltipLabel={iconTooltipLabel} />
                    </Flex>
                    <Flex flexDirection="column" gap="1px" maxHeight="25vh" overflowY="auto">
                      {modelOptions.map((option) => (
                        <Button
                          key={option.source}
                          title={option.value}
                          backgroundColor={theme.colors.surface1}
                          {...sharedButtonStyle}
                          onClick={() => {
                            handleSetFieldValue(onChange, option.rawValue.toString(true));
                            setValue(
                              `${selectedSourceFieldName}.source` as FieldPath<PropertyGridForm>,
                              option.source,
                            );
                            handleOnClose();
                          }}
                        >
                          <Flex alignItems="center" justifyContent="space-between" width="100%">
                            <Block
                              overflow="hidden"
                              __dangerDoNotUseSx={{
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              <Typography variant="body1">{option.value}</Typography>
                            </Block>
                            <Tooltip label={getSelectSourceTooltip(option.source)}>
                              <Icon
                                icon={getDataSourceDisplayInfo(option.source, undefined).icon}
                                color="buttonDefault"
                              />
                            </Tooltip>
                          </Flex>
                        </Button>
                      ))}
                    </Flex>
                  </PopoverContent>
                </>
              );
            }}
          </Popover>
        );
      }}
    />
  );
};

export const EditableUnitRateField = (props: PropertyGridFieldProps) => {
  const { field, colSpan = 1 } = props;
  const { unitRate } = field;

  if (!unitRate) return <UnitRatePlaceholderField colSpan={colSpan} />;
  return <EditableUnitRateFieldInner {...props} />;
};
